import axios from 'axios';

import { getNotificationsApiUrl } from 'helpers/url';

import {
    NotificationChannel,
    NotificationChannelBinding,
    NotificationChannelsReplaceEntry,
    NotificationFeed,
    NotificationTokenPair,
    NotificationTopic,
    NotificationTopicSlug,
} from 'types/notifications';

// Thin transport layer for the aquarius-notifications service. Auth/session
// state lives in NotificationsService; every authorized call receives the
// current access token explicitly.

const JSON_HEADERS = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
};

const authHeaders = (accessToken: string) => ({
    ...JSON_HEADERS,
    Authorization: `Bearer ${accessToken}`,
});

const apiUrl = () => getNotificationsApiUrl();

export interface Sep10Challenge {
    transaction: string;
    network_passphrase: string;
}

export const getSep10Challenge = (accountId: string): Promise<Sep10Challenge> =>
    axios
        .get<Sep10Challenge>(`${apiUrl()}/api/auth/sep10/challenge/`, {
            params: { account: accountId },
            headers: JSON_HEADERS,
        })
        .then(({ data }) => data);

export const verifySep10 = (signedXdr: string): Promise<NotificationTokenPair> =>
    axios
        .post<NotificationTokenPair>(
            `${apiUrl()}/api/auth/sep10/verify/`,
            JSON.stringify({ transaction: signedXdr }),
            { headers: JSON_HEADERS },
        )
        .then(({ data }) => data);

export const refreshTokenPair = (refreshToken: string): Promise<NotificationTokenPair> =>
    axios
        .post<NotificationTokenPair>(
            `${apiUrl()}/api/auth/token/refresh/`,
            JSON.stringify({ refresh_token: refreshToken }),
            { headers: JSON_HEADERS },
        )
        .then(({ data }) => data);

export const logoutSession = (refreshToken: string): Promise<void> =>
    axios
        .post(`${apiUrl()}/api/auth/logout/`, JSON.stringify({ refresh_token: refreshToken }), {
            headers: JSON_HEADERS,
        })
        .then(() => undefined);

export const getTopics = (): Promise<NotificationTopic[]> =>
    axios
        .get<NotificationTopic[]>(`${apiUrl()}/api/subscriptions/topics/`, {
            headers: JSON_HEADERS,
        })
        .then(({ data }) => data);

export const getChannels = (accessToken: string): Promise<NotificationChannelBinding[]> =>
    axios
        .get<NotificationChannelBinding[]>(`${apiUrl()}/api/account/channels/`, {
            headers: authHeaders(accessToken),
        })
        .then(({ data }) => data);

export const startChannelLink = (
    accessToken: string,
    channel: NotificationChannel,
    address: string,
): Promise<void> =>
    axios
        .post(
            `${apiUrl()}/api/account/channels/link/start/`,
            JSON.stringify({ channel, address }),
            { headers: authHeaders(accessToken) },
        )
        .then(() => undefined);

export const confirmChannelLink = (
    accessToken: string,
    code: string,
    emailAddress?: string,
): Promise<NotificationChannelBinding> => {
    const body = emailAddress
        ? { code, channel: NotificationChannel.email, address: emailAddress }
        : { code };

    return axios
        .post<NotificationChannelBinding>(
            `${apiUrl()}/api/account/channels/link/confirm/`,
            JSON.stringify(body),
            { headers: authHeaders(accessToken) },
        )
        .then(({ data }) => data);
};

export const unbindChannel = (accessToken: string, bindingId: number): Promise<void> =>
    axios
        .post(`${apiUrl()}/api/account/channels/${bindingId}/unbind/`, null, {
            headers: authHeaders(accessToken),
        })
        .then(() => undefined);

export const replaceChannelsTopics = (
    accessToken: string,
    entries: NotificationChannelsReplaceEntry[],
): Promise<NotificationChannelBinding[]> =>
    axios
        .post<
            NotificationChannelBinding[]
        >(`${apiUrl()}/api/account/subscriptions/channels/replace/`, JSON.stringify(entries), { headers: authHeaders(accessToken) })
        .then(({ data }) => data);

export const setBindingTopics = (
    accessToken: string,
    bindingId: number,
    topics: NotificationTopicSlug[],
    mode: 'set' | 'subscribe' | 'unsubscribe' = 'set',
): Promise<{ topics: NotificationTopicSlug[] }> =>
    axios
        .post<{
            topics: NotificationTopicSlug[];
        }>(
            `${apiUrl()}/api/account/subscriptions/${bindingId}/topics/`,
            JSON.stringify({ mode, topics }),
            { headers: authHeaders(accessToken) },
        )
        .then(({ data }) => data);

export const getFeed = (accessToken: string, limit?: number): Promise<NotificationFeed> =>
    axios
        .get<NotificationFeed>(`${apiUrl()}/api/account/notifications/`, {
            params: limit ? { limit } : undefined,
            headers: authHeaders(accessToken),
        })
        .then(({ data }) => data);

export interface NotificationFeedSettings {
    enabled: boolean;
    topics: NotificationTopicSlug[];
    timezone?: string;
}

export const getFeedSettings = (accessToken: string): Promise<NotificationFeedSettings> =>
    axios
        .get<NotificationFeedSettings>(`${apiUrl()}/api/account/notifications/settings/`, {
            headers: authHeaders(accessToken),
        })
        .then(({ data }) => data);

export const setFeedEnabled = (
    accessToken: string,
    enabled: boolean,
): Promise<NotificationFeedSettings> =>
    axios
        .post<NotificationFeedSettings>(
            `${apiUrl()}/api/account/notifications/settings/`,
            JSON.stringify({ enabled }),
            { headers: authHeaders(accessToken) },
        )
        .then(({ data }) => data);

export const setFeedTimezone = (
    accessToken: string,
    timezone: string,
): Promise<NotificationFeedSettings> =>
    axios
        .post<NotificationFeedSettings>(
            `${apiUrl()}/api/account/notifications/settings/`,
            JSON.stringify({ timezone }),
            { headers: authHeaders(accessToken) },
        )
        .then(({ data }) => data);

export const setFeedTopics = (
    accessToken: string,
    topics: NotificationTopicSlug[],
): Promise<NotificationFeedSettings> =>
    axios
        .post<NotificationFeedSettings>(
            `${apiUrl()}/api/account/notifications/settings/`,
            JSON.stringify({ topics }),
            { headers: authHeaders(accessToken) },
        )
        .then(({ data }) => data);

export const clearFeed = (accessToken: string): Promise<void> =>
    axios
        .post(`${apiUrl()}/api/account/notifications/clear/`, null, {
            headers: authHeaders(accessToken),
        })
        .then(() => undefined);

export const markFeedRead = (accessToken: string): Promise<void> =>
    axios
        .post(`${apiUrl()}/api/account/notifications/mark-read/`, null, {
            headers: authHeaders(accessToken),
        })
        .then(() => undefined);

export const sendTestNotification = (accessToken: string): Promise<void> =>
    axios
        .post(`${apiUrl()}/api/account/notifications/test/`, null, {
            headers: authHeaders(accessToken),
        })
        .then(() => undefined);
